import React, { useEffect, useState } from 'react';

import { Clear } from '@mui/icons-material';
import { Grid, IconButton, InputAdornment, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useDebouncedCallback } from 'use-debounce';

import { PRIMARY_GRAY } from 'commons/styles/colors';
import { IBin, IInboundTaskProcessedItem } from 'commons/types';

import S from './ReceiveProcessForm.style';

interface IReceiveItemFormProps {
  index: number;
  item: IInboundTaskProcessedItem;
  maxQuantityToAdd?: number;

  /** will be called if all field is filled */
  onAllFilled: (index: number, newItem: IInboundTaskProcessedItem) => void;

  /** will be called on each update on the field */
  verifyBin: (params: {
    bin: string;
    index: number;
  }) => Promise<Pick<IBin, 'name' | 'bin_type'>>;

  /** will be called on replace item */
  onRemove?: (index: number, newItem: IInboundTaskProcessedItem) => void;
}

const ReceiveProcessForm: React.FC<IReceiveItemFormProps> = ({
  item,
  index,
  maxQuantityToAdd,
  onAllFilled,
  verifyBin,
  onRemove,
}) => {
  const [_item, _setItem] = useState<IInboundTaskProcessedItem>({
    bin: item.bin,
    bin_type: item.bin_type,
    inventory_number: item.inventory_number,
    quantity: item.quantity,
    expiry_date: item.expiry_date,
  });

  useEffect(() => {
    _setItem({
      bin: item.bin,
      bin_type: item.bin_type,
      inventory_number: item.inventory_number,
      quantity: item.quantity,
      expiry_date: item.expiry_date,
    });
  }, [item]);

  const _debouncedHandleVerifyBin = useDebouncedCallback(
    async (__item: IInboundTaskProcessedItem): Promise<void> => {
      const verifyResult = await verifyBin({ index, bin: __item.bin });
      const { name, bin_type: binType } = verifyResult;
      _setItem({
        ...__item,
        bin: name,
        bin_type: binType,
      });
    },
    500,
    { maxWait: 2000 },
  );

  const _handleFullyFilledFields = async (
    __item: IInboundTaskProcessedItem,
  ): Promise<void> => {
    if (
      onAllFilled &&
      __item &&
      __item.bin &&
      __item.inventory_number &&
      __item.expiry_date &&
      __item.quantity &&
      (!item ||
        !(
          __item.bin === item.bin &&
          __item.inventory_number === item.inventory_number &&
          __item.expiry_date === item.expiry_date &&
          __item.quantity === item.quantity
        ))
    ) {
      onAllFilled(index, {
        bin: __item.bin,
        bin_type: __item.bin_type,
        expiry_date: __item.expiry_date,
        inventory_number: __item.inventory_number,
        quantity: __item.quantity,
      });
    }
  };

  const debouncedFocusNextField = useDebouncedCallback((selectorId: string) => {
    const nextInput = document.querySelector<HTMLInputElement>(selectorId);
    if (nextInput) nextInput.focus();
  }, 500);

  const onFieldChange = (
    _type: keyof IInboundTaskProcessedItem,
    value: string | number | Date | null,
    nextField?: string,
  ): void => {
    let _newItem = structuredClone(_item);
    const prevValue = _newItem[_type];
    let newValue = typeof value === 'string' ? value.trim() : value;
    if (
      _type === 'quantity' &&
      value !== '' &&
      value !== undefined &&
      value !== '0' &&
      value !== 0
    ) {
      const parsedValue =
        typeof value === 'string' ? parseInt(value, 10) : (value as number);
      if (parsedValue >= 0) {
        newValue =
          maxQuantityToAdd !== undefined && parsedValue >= maxQuantityToAdd
            ? maxQuantityToAdd
            : parsedValue;
      } else {
        newValue = 0;
      }
      _newItem = { ..._newItem, quantity: newValue };
    } else if (
      _type === 'quantity' &&
      (value === '' || value === undefined || value === '0' || value === 0)
    ) {
      _newItem = { ..._newItem, quantity: 0 };
      if (onRemove) {
        // to avoid call handlePartialFilledFields, return after remove batch
        onRemove(index, _newItem);
        _setItem(_newItem);
        return;
      }
    } else if (
      (_type === 'bin' || _type === 'inventory_number') &&
      _newItem.quantity
    ) {
      _newItem = {
        ..._newItem,
        bin: '',
        inventory_number: '',
        quantity: 0,
      };
      // _newItem[_type] = newValue as string;
      if (onRemove) {
        // to avoid call handlePartialFilledFields, return after remove batch
        onRemove(index, _newItem);
        _setItem(_newItem);
        return;
      }
    } else {
      _newItem = { ..._newItem, [_type]: newValue };
    }
    _setItem(_newItem);

    // verify bin
    if (!!verifyBin && _type === 'bin' && !!value) {
      _debouncedHandleVerifyBin(_newItem);
    }

    // check if field is fully filled
    // will trigger onAllFilled to api
    _handleFullyFilledFields(_newItem);

    if (
      nextField &&
      value &&
      typeof value === 'string' &&
      (value.endsWith('\t') ||
        value.endsWith('\n') ||
        (!prevValue && value.length > 1))
    ) {
      debouncedFocusNextField(
        `input[id="field-receive-${nextField}-${index}"]`,
      );
    }
  };

  return (
    <Grid container style={{ padding: '24px 0' }}>
      <Grid item xs={1} />
      <Grid item xs={11}>
        <Grid container>
          <Grid item xs={5}>
            <p>Bin</p>
          </Grid>
          <S.InputWrapper item xs={7}>
            <TextField
              variant="outlined"
              placeholder="-"
              value={_item.bin || ''}
              onChange={(event): void =>
                onFieldChange('bin', event.target.value, 'batch')
              }
              id={`field-receive-bin-${index}`}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => onFieldChange('bin', '', 'batch')}
                    >
                      <Clear style={{ color: PRIMARY_GRAY }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
            />
          </S.InputWrapper>
          <Grid item xs={5}>
            <p>Batch Number</p>
          </Grid>
          <S.InputWrapper item xs={7}>
            <TextField
              variant="outlined"
              placeholder="-"
              value={_item.inventory_number || ''}
              onChange={(event): void =>
                onFieldChange(
                  'inventory_number',
                  event.target.value,
                  'quantity',
                )
              }
              id={`field-receive-batch-${index}`}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        onFieldChange('inventory_number', '', 'quantity')
                      }
                    >
                      <Clear style={{ color: PRIMARY_GRAY }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
            />
          </S.InputWrapper>
          <Grid item xs={5}>
            <p>Expiry Date</p>
          </Grid>
          <S.InputWrapper item xs={7}>
            <DatePicker
              defaultValue={null}
              value={
                _item.expiry_date ? DateTime.fromISO(_item.expiry_date) : null
              }
              onChange={(value) => {
                onFieldChange('expiry_date', value?.toString() || null);
              }}
              format="dd/MM/yyyy"
              formatDensity="spacious"
              slotProps={{
                textField: {
                  id: `field-receive-expiry-${index}`,
                  variant: 'outlined',
                  fullWidth: true,
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => onFieldChange('expiry_date', '')}
                        >
                          <Clear style={{ color: PRIMARY_GRAY }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { paddingRight: 0 },
                  },
                },
              }}
            />
          </S.InputWrapper>
          <Grid item xs={5}>
            <p>Receive Quantity</p>
          </Grid>
          <S.InputWrapper item xs={7}>
            <TextField
              variant="outlined"
              placeholder="0"
              value={_item.quantity || ''}
              type="number"
              onChange={(event): void =>
                onFieldChange('quantity', event.target.value)
              }
              id={`field-receive-quantity-${index}`}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => onFieldChange('quantity', '0')}>
                      <Clear style={{ color: PRIMARY_GRAY }} />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { paddingRight: 0 },
              }}
            />
          </S.InputWrapper>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ReceiveProcessForm;
